import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SidebarContainer, SidebarHeader, SidebarMenu, SidebarMenuItem, SidebarMenuItemDiffer, SidebarFooter, SidebarIcon, SidebarLucideIcon, SettingsHintBadge, MobileMenuButton, MobileSidebarBackdrop } from "./styles";
import { appStatus } from "../../store/modules/app_status/actions";
import { collapseSidebar } from "../../store/modules/sidebar/actions";
import {
  goToPanel,
  isGuardianRoute,
  GUARDIAN_PANEL,
  WELCOME_PATH,
  PANEL_ROUTES,
} from "../../utils/appNavigation";
import ExpandedLogo from "../../assets/logo-vert.png";
import ArrowRight from "../../assets/icons/vert/arrow-right-vert2.png";
import ArrowLeft from "../../assets/icons/vert/arrow-left-vert2.png";
import Logo from "../../assets/marca-vert.png";
import Leaf from "../../assets/icons/vert/leaf-vert.png";
import Escudo from "../../assets/icons/vert/guardian-vert2.png";
import Configuracoes from "../../assets/icons/vert/settings-vert2.png";
import Dashboard from "../../assets/icons/vert/dashboard-vert2.png";
import Users from "../../assets/icons/vert/users-vert3.png";
import myUser from '../../assets/icons/vert/me-vert2.png';
import { userLogin } from "../../store/modules/login/actions";
import { userService } from "../../services/userService";
import { projectService } from "../../services/projectService";
import { universalService } from "../../services/universalService";
import { hasSeenLightModeHint } from "../../utils/themeFeatureHint";
import { useThemeMode } from "../../contexts/ThemeContext";
import { persistUiSession } from "../../utils/authSession";
import {
  canAccessDashboard,
  canAccessGuardian,
  canAccessUsersPanel,
  isAdminUser,
  isAdminOrEngineeringUser,
  isPropOrGuardianUser,
} from "../../utils/userRoles";

/** Painéis do shell /welcome — sempre navegam para /welcome + app_status. */
const WELCOME_PANEL_STATUSES = new Set([
  "Dashboard",
  "Meus Alertas",
  "Usuários",
  "Projetos",
  "Meu Perfil",
  "Configurações",
  GUARDIAN_PANEL,
]);

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { applyThemeFromAuth } = useThemeMode();
  const app_status = useSelector((state) => state.app_status.status);
  const collapsed = useSelector((state) => state.sidebar);
  const currentUser = useSelector((state) => state.user.currentUser);
  const showDashboard = canAccessDashboard(currentUser);
  const showMyAlerts = isPropOrGuardianUser(currentUser);
  const showGuardian = canAccessGuardian(currentUser);

  const handleLogin = async () => {
    if (!currentUser) {
      try {
        const response = await userService.getMe();
        dispatch(userLogin(null, response, response.id));
        applyThemeFromAuth(response);
        persistUiSession(response);

        await Promise.all([
          userService.getUsers(),
          projectService.getProjects(),
          universalService.getEngineeringTable(),
        ]);
      } catch (error) {
        console.error("❌ Erro ao recuperar sessão:", error);
      }
    }
  };

  const currentYear = new Date().getFullYear();
  // Chame a função assíncrona handleLogin em algum ponto apropriado do seu código

  const setCollapsed = (state) => {
    dispatch(collapseSidebar(state));
  };

  const handleItemClick = (status) => {
    setMobileOpen(false);

    // Painéis principais: navegam para a URL real (ex.: /dashboard, /guardian)
    if (WELCOME_PANEL_STATUSES.has(status)) {
      const targetRoute = PANEL_ROUTES[status];
      // Já na rota do painel — evita navegação redundante
      if (targetRoute && location.pathname === targetRoute) {
        return;
      }
      goToPanel(dispatch, navigate, status);
      return;
    }

    dispatch(appStatus(status));
    if (location.pathname !== WELCOME_PATH) {
      navigate(WELCOME_PATH);
    }
  };

  const [activeDashboard, setActiveDashboard] = useState(false);
  const [activeUser, setActiveUser] = useState(false);
  const [activeUsers, setActiveUsers] = useState(false);
  const [activeShoppingCart, setActiveShoppingCart] = useState(false);
  const [activeSettings, setActiveSettings] = useState(false);
  const [activeGuardian, setActiveGuardian] = useState(false);
  const [showLightModeHint, setShowLightModeHint] = useState(
    () => !hasSeenLightModeHint()
  );

  useEffect(() => {
    if (hasSeenLightModeHint()) {
      setShowLightModeHint(false);
      return;
    }
    // Esconde o badge enquanto o usuário está em Configurações.
    setShowLightModeHint(app_status !== "Configurações");
  }, [app_status]);

  useEffect(() => {
    const hideHint = () => setShowLightModeHint(false);
    window.addEventListener("vert-light-mode-hint-seen", hideHint);
    return () => {
      window.removeEventListener("vert-light-mode-hint-seen", hideHint);
    };
  }, []);

  const handleActiveIcon = () => {
    if (app_status === "Dashboard") {
      setActiveDashboard(true);
    } else {
      setActiveDashboard(false);
    }
    if (app_status === "Usuários") {
      setActiveUsers(true);
    } else {
      setActiveUsers(false);
    }
    if (app_status === "Pedidos") {
      setActiveShoppingCart(true);
    } else {
      setActiveShoppingCart(false);
    }
    if (app_status === "Meu Perfil") {
      setActiveUser(true);
    } else {
      setActiveUser(false);
    }
    if (app_status === "Configurações") {
      setActiveSettings(true);
    } else {
      setActiveSettings(false);
    }
    if (app_status === GUARDIAN_PANEL || isGuardianRoute(location.pathname)) {
      setActiveGuardian(true);
    } else {
      setActiveGuardian(false);
    }
  };

  useEffect(() => {
    handleActiveIcon();
    handleLogin();

  }, [app_status, currentUser, location.pathname]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setMobileOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const handleToggleSidebar = () => {
    dispatch(collapseSidebar(!collapsed));
  };

  /** No drawer mobile sempre mostra rótulos (não só ícones). */
  const navCollapsed = isMobile ? false : collapsed;

  return (
    <>
      <MobileMenuButton
        type="button"
        aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
        aria-expanded={mobileOpen}
        onClick={() => setMobileOpen((open) => !open)}
      >
        {mobileOpen ? "✕" : "☰"}
      </MobileMenuButton>
      <MobileSidebarBackdrop
        type="button"
        aria-label="Fechar menu"
        $open={mobileOpen}
        onClick={() => setMobileOpen(false)}
      />
        <SidebarContainer
          collapsed={navCollapsed}
          $open={mobileOpen}
        >
          <SidebarHeader
            onClick={() => {
              if (isMobile) {
                setMobileOpen(false);
                return;
              }
              handleToggleSidebar();
            }}
          >
          <AnimatePresence mode="wait" initial={false}>
            <motion.img
              key={navCollapsed ? "logo-collapsed" : "logo-expanded"}
              src={navCollapsed ? Logo : ExpandedLogo}
              alt="Logo"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.3 }}
              style={{
                width: isMobile
                  ? "min(70%, 180px)"
                  : navCollapsed
                    ? "1.98vw"
                    : "10.4vw",
                maxWidth: isMobile ? "180px" : undefined,
                marginTop: isMobile ? "0.5rem" : "2.96vh",
                background: "transparent",
                padding: "0.74vh",
                filter: "var(--sidebar-logo-filter)",
              }}
            />
          </AnimatePresence>
        </SidebarHeader>

        <SidebarMenu>
          {showDashboard && (
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <SidebarMenuItem
                collapsed={navCollapsed}
                data-label="Dashboard"
                className={app_status === "Dashboard" ? "active" : ""}
                onClick={() => handleItemClick("Dashboard")}
              >
                {navCollapsed ? (
                  <SidebarIcon src={Dashboard} alt="" />
                ) : (
                  "Dashboard"
                )}
              </SidebarMenuItem>
            </motion.div>
          </AnimatePresence>
          )}

          {showMyAlerts && (
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <SidebarMenuItem
                collapsed={navCollapsed}
                data-label="Meus Alertas"
                className={
                  app_status === "Meus Alertas" ||
                  location.pathname.startsWith("/my-alerts")
                    ? "active"
                    : ""
                }
                onClick={() => handleItemClick("Meus Alertas")}
              >
                {navCollapsed ? <SidebarLucideIcon aria-hidden /> : "Meus Alertas"}
              </SidebarMenuItem>
            </motion.div>
          </AnimatePresence>
          )}

          {canAccessUsersPanel(currentUser) && (
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <SidebarMenuItem
                  collapsed={navCollapsed}
                  data-label="Usuários"
                  className={app_status === "Usuários" ? "active" : ""}
                  onClick={() => handleItemClick("Usuários")}
                >
                  {navCollapsed ? (
                    <SidebarIcon src={Users} alt="" />
                  ) : (
                    "Usuários"
                  )}
                </SidebarMenuItem>
              </motion.div>
            </AnimatePresence>
          )}

          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              {isAdminOrEngineeringUser(currentUser) ? (
                <SidebarMenuItem
                  collapsed={navCollapsed}
                  data-label="Projetos"
                  className={app_status === "Projetos" ? "active" : ""}
                  onClick={() => handleItemClick("Projetos")}
                >
                  {navCollapsed ? <SidebarIcon src={Leaf} alt="" /> : "Projetos"}
                </SidebarMenuItem>
              ) : (
                <SidebarMenuItem
                  collapsed={navCollapsed}
                  data-label="Meus Projetos"
                  className={app_status === "Projetos" ? "active" : ""}
                  onClick={() => handleItemClick("Projetos")}
                >
                  {navCollapsed ? (
                    <SidebarIcon src={Leaf} alt="" />
                  ) : (
                    "Meus Projetos"
                  )}
                </SidebarMenuItem>
              )}
            </motion.div>
          </AnimatePresence>

          {/** guardian — ADM, ENG, PROP e GUARDIAN */}
          {showGuardian && (
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <SidebarMenuItem
                  collapsed={navCollapsed}
                  data-label="Guardian"
                  className={
                    app_status === GUARDIAN_PANEL || isGuardianRoute(location.pathname)
                      ? "active"
                      : ""
                  }
                  onClick={() => handleItemClick(GUARDIAN_PANEL)}
                >
                  {navCollapsed ? (
                    <SidebarIcon src={Escudo} alt="" />
                  ) : (
                    "Guardian"
                  )}
                </SidebarMenuItem>
              </motion.div>
            </AnimatePresence>
          )}


          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.4 }}
            >
              <SidebarMenuItem
                collapsed={navCollapsed}
                data-label="Meu Perfil"
                className={app_status === "Meu Perfil" ? "active" : ""}
                onClick={() => handleItemClick("Meu Perfil")}
              >
                {navCollapsed ? (

                  <SidebarIcon src={myUser} alt="" />
                ) : (
                  "Meu Perfil"
                )}
              </SidebarMenuItem>
            </motion.div>
          </AnimatePresence>

          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
            >
              <SidebarMenuItem
                collapsed={navCollapsed}
                data-label="Configurações"
                className={app_status === "Configurações" ? "active" : ""}
                onClick={() => handleItemClick("Configurações")}
                $featureHint={showLightModeHint}
              >
                {showLightModeHint ? (
                  <SettingsHintBadge $collapsed={navCollapsed}>Novo</SettingsHintBadge>
                ) : null}
                {navCollapsed ? (
                  <SidebarIcon src={Configuracoes} alt="" />
                ) : (
                  "Configurações"
                )}
              </SidebarMenuItem>
            </motion.div>
          </AnimatePresence>
        </SidebarMenu>



        <SidebarFooter >
          <SidebarMenuItemDiffer onClick={() => setCollapsed(!collapsed)}>
            <AnimatePresence mode="wait" initial={false}>
              {navCollapsed ? (
                <motion.img
                  key="arrow-right"
                  src={ArrowRight}
                  alt="arrow-right"
                  style={{
                    alignItems: "center",
                    width: "1.2vw",
                  }}
                  initial={{ opacity: 0, rotate: -90, y: -5 }}
                  animate={{ opacity: 1, rotate: 0, y: 0 }}
                  exit={{ opacity: 0, rotate: 90, y: 5 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                />
              ) : (
                <motion.img
                  key="arrow-left"
                  src={ArrowLeft}
                  alt="arrow-left"
                  style={{
                    alignItems: "center",
                    width: "1.2vw",
                  }}
                  initial={{ opacity: 0, rotate: 90, y: 5 }}
                  animate={{ opacity: 1, rotate: 0, y: 0 }}
                  exit={{ opacity: 0, rotate: -90, y: -5 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                />
              )}
            </AnimatePresence>
          </SidebarMenuItemDiffer>

          <AnimatePresence mode="wait" initial={false}>
            {navCollapsed ? (
              <motion.span
                key="collapsed"
                style={{ color: "var(--text-primary)", fontSize: "1rem", marginBottom: "10px" }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                V.E &copy;
              </motion.span>
            ) : (
              <motion.span
                key="expanded"
                style={{ color: "var(--text-primary)", fontSize: "1rem", marginBottom: "10px" }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                Vert Ecotech &copy; {currentYear}
              </motion.span>
            )}
          </AnimatePresence>
        </SidebarFooter>
      </SidebarContainer>
    </>
  );
};


export default Sidebar;
